import type { App } from '../../app/App';
import type { OpenFailFlowPopupConfig, StartFailFlowConfig } from './startFailFlow';
import { FailPopup, FailPopupLayoutType, FailPopupStep } from '../../game/views/popups/failFlow/FailPopup';
import { exitToMainMenu } from '../exitToMainMenu';
import type { GameSceneOptions } from '../../game/scene/GameSceneOptions';
import { restartLevel } from '../restartLevel';
import { openOutOfLivesPopup } from '../openOutOfLivesPopup';
import type { MetaProperties } from '../../app/tracking/trackingModules/avo/Avo';

enum RetryFlowResolution {
  CancelRestart = 'CancelRestart',
  ExitToMenu = 'ExitToMenu',
  RestartLevel = 'RestartLevel',
}

async function openRetryPopup(config: OpenFailFlowPopupConfig): Promise<RetryFlowResolution> {
  const {
    app: {
      stateController,
      controller: { popups },
      gameEvents,
    },
    app,
    unlimitedLives,
    flowConfig: { metaProperties },
  } = config;

  const { resolve, promise } = Promise.withResolvers<RetryFlowResolution>();

  const failPopupView = new FailPopup({
    state: stateController,
    tasks: config.flowConfig.tasks,
    unlimitedLives,
    lossProgressBadges: gameEvents.createLostProgressBadges(),
    layoutType: FailPopupLayoutType.Retry,
  });

  void popups.addPopup({
    popupInstance: failPopupView,
  });

  let hasRestarted = false;

  failPopupView.onQuitButtonClicked.connect(async (step: FailPopupStep) => {
    if (step === FailPopupStep.Confirmation) {
      hasRestarted = true;
      onLevelLost(app, metaProperties);
    }

    await failPopupView.advancePopupStep();
  });

  failPopupView.onRetryButtonClicked.connect(async () => {
    if (stateController.getLives() === 0 && !unlimitedLives) {
      await openOutOfLivesPopup({
        app,
        entryPoint: 'fail-popup',
        viewsToMoveAbove: [],
      });
      if (!stateController.hasLivesToPlay()) return;
    }

    resolve(RetryFlowResolution.RestartLevel);
  });

  failPopupView.onCloseButtonClicked.connect(() => {
    resolve(hasRestarted ? RetryFlowResolution.ExitToMenu : RetryFlowResolution.CancelRestart);
  });

  return promise;
}

function onLevelLost(app: App, metaProperties?: MetaProperties[]): void {
  const { stateController, resources, tracking, gameEvents } = app;

  const unlimitedLives = stateController.getUnlimitedLivesMinutes() > 0;

  gameEvents.onLevelLost();

  tracking.trackLevelLost({
    ...stateController.getLevelTrackingData(),
    metaProperties: [...(metaProperties ?? [])],
  });

  if (!unlimitedLives) {
    resources.useLife();
  }

  stateController.resetLevel();
}

export interface RetryFlowConfig extends StartFailFlowConfig {
  sceneOptions: GameSceneOptions;
}

export async function startRetryFlow(app: App, config: RetryFlowConfig): Promise<void> {
  const {
    stateController,
    controller: { popups },
  } = app;

  const unlimitedLives = stateController.getUnlimitedLivesMinutes() > 0;

  const retryPopupResolution = await openRetryPopup({
    app,
    unlimitedLives,
    flowConfig: config,
  });

  popups.closeAllPopups();

  switch (retryPopupResolution) {
    case RetryFlowResolution.RestartLevel:
      if (stateController.hasLivesToPlay()) {
        restartLevel(app, config.sceneOptions);
      } else {
        exitToMainMenu(app);
      }
      break;
    case RetryFlowResolution.ExitToMenu:
      exitToMainMenu(app);
      break;
    case RetryFlowResolution.CancelRestart:
    default:
      break;
  }
}
